import { useId, useState } from "react";
import { Button } from "@design-system/Button/Button";
import type { SpreadsheetCell } from "@components/spreadsheet/types";
import { PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./SpreadsheetPlayground.module.scss";
import { NestedRowsScenario } from "./spreadsheetPlayground/NestedRowsScenario";
import { PinnedAndResizableScenario } from "./spreadsheetPlayground/PinnedAndResizableScenario";
import { TotalsScenario } from "./spreadsheetPlayground/TotalsScenario";
import {
    cloneLineItems,
    cloneNestedLines,
} from "./spreadsheetPlayground/spreadsheetPlaygroundShared";

type ScenarioId = "pinned" | "nested" | "totals";

const SCENARIOS = [
    { value: "pinned", label: "Pinned and resizable" },
    { value: "nested", label: "Nested rows" },
    { value: "totals", label: "Totals row" },
] satisfies ReadonlyArray<{ value: ScenarioId; label: string }>;

const DEFAULT_EXPANDED_IDS = ["operations", "month-end"];

/**
 * @purpose Showcases `SpreadsheetGrid` one capability at a time: pinned and
 * resizable columns with a sticky header, nested expand/collapse rows, and a
 * totals row. Scenario state is kept here so switching scenarios never discards
 * edits, while Reset restores the starting fixtures.
 */
export const SpreadsheetPlayground = () => {
    const scenarioLabelId = useId();
    const [activeScenario, setActiveScenario] = useState<ScenarioId>("pinned");
    const [pinnedRows, setPinnedRows] = useState(cloneLineItems);
    const [pinnedSelectedCell, setPinnedSelectedCell] =
        useState<SpreadsheetCell | null>(null);
    const [nestedRows, setNestedRows] = useState(cloneNestedLines);
    const [expandedIds, setExpandedIds] =
        useState<string[]>(DEFAULT_EXPANDED_IDS);
    const [totalsRows, setTotalsRows] = useState(cloneLineItems);

    const handleReset = () => {
        setPinnedRows(cloneLineItems());
        setPinnedSelectedCell(null);
        setNestedRows(cloneNestedLines());
        setExpandedIds(DEFAULT_EXPANDED_IDS);
        setTotalsRows(cloneLineItems());
    };

    const renderScenario = () => {
        switch (activeScenario) {
            case "pinned":
                return (
                    <PinnedAndResizableScenario
                        data={pinnedRows}
                        selectedCell={pinnedSelectedCell}
                        onChange={setPinnedRows}
                        onSelectedCellChange={setPinnedSelectedCell}
                    />
                );
            case "nested":
                return (
                    <NestedRowsScenario
                        data={nestedRows}
                        expandedIds={expandedIds}
                        onExpandedChange={setExpandedIds}
                    />
                );
            case "totals":
                return (
                    <TotalsScenario
                        data={totalsRows}
                        onChange={setTotalsRows}
                    />
                );
        }
    };

    const activeScenarioLabel = SCENARIOS.find(
        (scenario) => scenario.value === activeScenario,
    )?.label;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2 id={scenarioLabelId}>Scenario</h2>
                        <PlaygroundSelect<ScenarioId>
                            options={SCENARIOS}
                            value={activeScenario}
                            onChange={setActiveScenario}
                            aria-labelledby={scenarioLabelId}
                        />
                    </section>
                    <section>
                        <h2>Showcase state</h2>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={handleReset}
                        >
                            Reset
                        </Button>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={activeScenarioLabel}
                width="full"
                alignment="stretch"
            >
                <div className={styles.scenarioStage}>{renderScenario()}</div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
